import { ReactNode } from 'react';
import { Divider, Drawer, Select, Tooltip } from 'antd';
import { AiOutlineFile, AiOutlineHome, AiOutlineLogout, AiOutlineWarning, AiOutlineBank } from 'react-icons/ai';
import { HiOutlineChevronLeft } from 'react-icons/hi';
import { BsBuilding, BsGearFill, BsReceipt } from 'react-icons/bs';
import { HiSelector } from 'react-icons/hi';
import { Link, useHistory, useLocation } from 'react-router-dom';

import { capitalize } from '../../../../helpers/capitalize';
import { useAuth } from '../../../../hooks/useAuth';
import { useDividasCount } from '../../../../hooks/useDividasCount';
import { Logo } from '../../../Common/Logo';
import { PulsingDot } from '../../../Common/PulsingDot';
import {
  CloseButton,
  CompanySelect,
  CompanySelectContainer,
  CustomerName,
  DrawerContent,
  LogoutButton,
  NavItem,
  UserAvatar,
  UserInfo,
  UserName,
  UserSection,
} from './styles';

interface NavigationItem {
  key: string;
  label: string;
  icon: ReactNode;
  /** Texto do tooltip do ponto vermelho; sem texto, sem ponto. */
  alert?: string;
}

interface SidebarDrawerProps {
  isVisible: boolean;
  onClose: () => void;
}

export const SidebarDrawer = ({ isVisible, onClose }: SidebarDrawerProps) => {
  const history = useHistory();
  const location = useLocation();
  const { signOut, user, signInByApiToken } = useAuth();
  const dividasCount = useDividasCount();
  const dividasAlert = dividasCount > 0
    ? `Você tem ${dividasCount} ${dividasCount === 1 ? 'pendência tributária' : 'pendências tributárias'}`
    : undefined;

  const navigationItems: NavigationItem[] = [
    { key: '/', label: 'Início', icon: <AiOutlineHome /> },
    { key: '/arquivos', label: 'Documentos', icon: <AiOutlineFile /> },
    { key: '/dividas-tributarias', label: 'Pendências Tributárias', icon: <AiOutlineWarning />, alert: dividasAlert },
    ...(user?.extrato_bancario ? [{ key: '/extrato-bancario', label: 'Extrato Bancário', icon: <AiOutlineBank /> }] : []),
    ...(user?.allow_nf ? [{ key: '/solicitacoes', label: 'Notas Fiscais', icon: <BsReceipt /> }] : []),
    { key: '/configuracoes', label: 'Configurações', icon: <BsGearFill /> },
  ];

  const handleLogout = () => {
    signOut();
    history.push('/login');
  };

  const handleNavigation = (path: string) => {
    history.push(path);
    onClose();
  };

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const currentCompanyId = 'current';
  const companies = user
    ? [
        { id: currentCompanyId, name: user.customer_name },
        ...(user.other_customers || []).map((c) => ({
          id: c.id.toString(),
          name: c.name,
          api_token: c.api_token,
        })),
      ]
    : [];

  const handleCompanyChange = async (value: string) => {
    const company = companies.find((c) => c.id === value);
    if (company && 'api_token' in company && company.api_token) {
      await signInByApiToken(company.api_token);
      onClose();
    }
  };

  return (
    <Drawer
      placement="left"
      open={isVisible}
      onClose={onClose}
      width={280}
      closable={false}
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <DrawerContent>
        <div className="drawer-header">
          <Link to="/" onClick={onClose}>
            <Logo tenant height={28} />
          </Link>
          <CloseButton onClick={onClose}>
            <HiOutlineChevronLeft />
          </CloseButton>
        </div>

        {companies.length > 1 && (
          <div className="company-section">
            <CompanySelectContainer>
              <span className="company-icon">
                <BsBuilding />
              </span>
              <CompanySelect
                value={currentCompanyId}
                onChange={(value) => handleCompanyChange(value as string)}
                suffixIcon={<HiSelector />}
              >
                {companies.map((company) => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.name}
                  </Select.Option>
                ))}
              </CompanySelect>
            </CompanySelectContainer>
          </div>
        )}

        <Divider style={{ margin: '8px 0' }} />

        <div className="nav-section">
          {navigationItems.map((item) => (
            <NavItem
              key={item.key}
              $isActive={location.pathname === item.key}
              onClick={() => handleNavigation(item.key)}
            >
              {item.icon}
              {item.label}
              {item.alert && (
                <Tooltip title={item.alert} placement="left">
                  <PulsingDot />
                </Tooltip>
              )}
            </NavItem>
          ))}
        </div>

        <UserSection>
          <div className="user-info-row">
            <UserAvatar>{getUserInitials()}</UserAvatar>
            <UserInfo>
              <UserName>{user?.name || 'Usuário'}</UserName>
              <CustomerName>{capitalize(user?.customer_name || '')}</CustomerName>
            </UserInfo>
          </div>
          <LogoutButton onClick={handleLogout}>
            <AiOutlineLogout />
            Sair
          </LogoutButton>
        </UserSection>
      </DrawerContent>
    </Drawer>
  );
};
